import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: { main: '#2E7D32', light: '#4CAF50', dark: '#1B5E20', contrastText: '#fff' },
    secondary: { main: '#FF8F00', light: '#FFB300', dark: '#E65100', contrastText: '#fff' },
    background: { default: '#F4F8F4', paper: '#ffffff' },
    success: { main: '#388E3C' },
    error: { main: '#D32F2F' },
    warning: { main: '#F57C00' },
    info: { main: '#0288D1' },
    text: { primary: '#1C2B2D', secondary: '#5F7B80' },
    divider: 'rgba(46,125,50,0.1)',
  },
  typography: {
    fontFamily: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: { fontWeight: 800, fontSize: '2.5rem', letterSpacing: '-0.5px' },
    h2: { fontWeight: 700, fontSize: '2rem', letterSpacing: '-0.3px' },
    h3: { fontWeight: 700, fontSize: '1.75rem', letterSpacing: '-0.2px' },
    h4: { fontWeight: 600, fontSize: '1.5rem' },
    h5: { fontWeight: 600, fontSize: '1.25rem' },
    h6: { fontWeight: 600, fontSize: '1rem' },
    body1: { fontSize: '0.9375rem', lineHeight: 1.65 },
    body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    caption: { fontSize: '0.75rem', lineHeight: 1.5 },
    button: { fontWeight: 600, letterSpacing: '0.01em' },
  },
  shape: { borderRadius: 12 },
  shadows: [
    'none',
    '0 1px 4px rgba(0,0,0,0.06)',
    '0 2px 8px rgba(0,0,0,0.08)',
    '0 4px 12px rgba(0,0,0,0.08)',
    '0 4px 16px rgba(0,0,0,0.1)',
    '0 8px 24px rgba(0,0,0,0.1)',
    '0 8px 24px rgba(0,0,0,0.12)',
    '0 12px 32px rgba(0,0,0,0.12)',
    '0 16px 40px rgba(0,0,0,0.12)',
    '0 16px 40px rgba(0,0,0,0.14)',
    '0 20px 48px rgba(0,0,0,0.14)',
    '0 20px 48px rgba(0,0,0,0.16)',
    '0 24px 56px rgba(0,0,0,0.16)',
    '0 24px 56px rgba(0,0,0,0.18)',
    '0 28px 64px rgba(0,0,0,0.18)',
    '0 28px 64px rgba(0,0,0,0.2)',
    '0 32px 72px rgba(0,0,0,0.2)',
    '0 32px 72px rgba(0,0,0,0.22)',
    '0 36px 80px rgba(0,0,0,0.22)',
    '0 36px 80px rgba(0,0,0,0.24)',
    '0 40px 88px rgba(0,0,0,0.24)',
    '0 40px 88px rgba(0,0,0,0.26)',
    '0 44px 96px rgba(0,0,0,0.26)',
    '0 44px 96px rgba(0,0,0,0.28)',
    '0 48px 104px rgba(0,0,0,0.28)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': { boxSizing: 'border-box' },
        body: { backgroundColor: '#F4F8F4' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          fontFamily: '"Poppins", sans-serif',
          padding: '9px 20px',
          transition: 'all 0.2s ease',
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #2E7D32 0%, #43A047 100%)',
          boxShadow: '0 2px 8px rgba(46,125,50,0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)',
            boxShadow: '0 4px 14px rgba(46,125,50,0.4)',
          },
        },
        outlinedPrimary: {
          borderColor: 'rgba(46,125,50,0.5)',
          '&:hover': {
            borderColor: '#2E7D32',
            background: 'rgba(46,125,50,0.04)',
          },
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid rgba(46,125,50,0.08)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          '&:hover': {
            boxShadow: '0 6px 24px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': { paddingBottom: 16 },
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '& fieldset': { borderColor: 'rgba(46,125,50,0.2)' },
            '&:hover fieldset': { borderColor: 'rgba(46,125,50,0.4)' },
            '&.Mui-focused fieldset': { borderColor: '#2E7D32' },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 500,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#1C2B2D',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 12 },
        rounded: { borderRadius: 16 },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: 'rgba(46,125,50,0.08)' },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          fontSize: '12px',
          fontFamily: '"Poppins", sans-serif',
          background: 'rgba(28,43,45,0.92)',
        },
        arrow: { color: 'rgba(28,43,45,0.92)' },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10, fontFamily: '"Poppins", sans-serif' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { fontFamily: '"Poppins", sans-serif', fontSize: '13px' },
        head: { fontWeight: 700, fontSize: '12px' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontFamily: '"Poppins", sans-serif',
          fontSize: '13px',
        },
      },
    },
  },
});

export default theme;
